import { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User } from '@agency-smm/shared';
import { apiClient } from '../services/apiClient';

export interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  updateCredits: (newCredits: number) => void;
}

export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const userData = await apiClient.get<User>('/api/auth/me');
          setUser(userData);
        }
      } catch {
        localStorage.removeItem('token');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await apiClient.post<{ user: User; token: string }>('/api/auth/login', {
      email,
      password,
    });
    localStorage.setItem('token', response.token);
    setUser(response.user);
  }, []);

  const register = useCallback(async (email: string, password: string, name: string) => {
    const response = await apiClient.post<{ user: User; token: string }>('/api/auth/register', {
      email,
      password,
      name,
    });
    localStorage.setItem('token', response.token);
    setUser(response.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/api/auth/logout', {});
    } finally {
      localStorage.removeItem('token');
      setUser(null);
    }
  }, []);

  const updateCredits = useCallback((newCredits: number) => {
    setUser((prevUser) => {
      if (!prevUser) return null;
      return { ...prevUser, credits: newCredits };
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, register, logout, updateCredits }}>
      {children}
    </AuthContext.Provider>
  );
}
